import Vue from 'vue'
import Vuex from 'vuex'
import artistModule from './modules/artists'
import albumModule from './modules/album'
import trackModule from './modules/track'
import playlistModule from './modules/playlist'
import audiusModule from './modules/audius'
import sunoModule from './modules/suno'
import { ArtistTrackModel } from '@/models'

Vue.use(Vuex)

const PLAYER_VOLUME_KEY = 'playerVolume'
const BLACKLIST_KEY = 'blacklistedArtists'

function getBlacklist(): string[] {
    if (typeof window !== 'undefined') {
        const saved = window.localStorage.getItem(BLACKLIST_KEY)
        if (saved) {
            try { return JSON.parse(saved) } catch(e) { /* ignore */ }
        }
    }
    return []
}

function parseSavedVolume(value: string | null) {
  if (value === null) {
    return 1
  }

  const parsed = parseFloat(value)
  if (Number.isNaN(parsed)) {
    return 1
  }

  return Math.min(1, Math.max(0, parsed))
}

function createAudioElement() {
  const audio = new Audio()

  if (typeof window !== 'undefined') {
    audio.volume = parseSavedVolume(window.localStorage.getItem(PLAYER_VOLUME_KEY))
  }

  return audio
}

interface InterState {
    sideNav: boolean;
    audio: HTMLAudioElement;
    current: ArtistTrackModel | null;
    tracksQueue: ArtistTrackModel[];
    isShuffle: boolean;
    isRepeat: boolean;
    isRadio: boolean;
    radioGenre: string;
    showFull: boolean;
    blacklistedArtists: string[];
    radioSource: string;
    unavailableTrackIds: string[];
}

function getCurrentTrackIndex(state: InterState) {
  if (!state.current) {
    return -1
  }

  const currentTrackId = state.current.id
  return state.tracksQueue.findIndex((track: ArtistTrackModel) => track.id === currentTrackId)
}

function getRandomTrackIndex(state: InterState, currentIndex: number) {
  if (state.tracksQueue.length <= 1) {
    return 0
  }

  let randomIndex = currentIndex
  while (randomIndex === currentIndex) {
    randomIndex = Math.floor(Math.random() * state.tracksQueue.length)
  }

  return randomIndex
}

function createRadioOffset(limit: number, maxPages = 100) {
  // Use a bigger range for Jamendo to find deeper gems
  const randomSeed = Math.floor(Math.random() * maxPages)
  return randomSeed * limit
}

function rotateQueue<T>(tracks: T[], startIndex: number) {
  if (!tracks.length || startIndex <= 0) {
    return tracks
  }

  return tracks.slice(startIndex).concat(tracks.slice(0, startIndex))
}

function isSunoTrack(track: ArtistTrackModel) {
  return track.id.indexOf('suno-') === 0 || track.audio.indexOf('suno.ai') !== -1
}

function isUnavailableTrack(state: InterState, track: ArtistTrackModel) {
  return state.unavailableTrackIds.includes(track.id)
}

async function hasPlayableSource(track: ArtistTrackModel) {
  if (!track.audio) {
    return false
  }

  if (!isSunoTrack(track)) {
    return true
  }

  try {
    const response = await fetch(`/api/suno/check?url=${encodeURIComponent(track.audio)}`, {
      method: 'GET',
      cache: 'no-store',
    })

    if (response.status === 404) {
      return true
    }

    if (!response.ok) {
      return false
    }

    const result = await response.json()
    return result && result.ok === true
  } catch (error) {
    return true
  }
}

async function findPlayableTrack(state: InterState, commit: Function, tracks: ArtistTrackModel[]) {
  for (const track of tracks) {
    if (isUnavailableTrack(state, track)) {
      continue
    }

    if (await hasPlayableSource(track)) {
      return track
    }

    commit('markTrackUnavailable', track)
  }

  return null
}

const state = {
  sideNav: false,
  audio: createAudioElement(),
  current: null,
  tracksQueue: [],
  isShuffle: false,
  isRepeat: false,
  isRadio: false,
  radioGenre: '',
  showFull: false,
  blacklistedArtists: getBlacklist(),
  radioSource: (typeof window !== 'undefined' ? window.localStorage.getItem('radioSource') : null) || 'audius',
  unavailableTrackIds: [],
}

const mutations = {
  setSideNav(state: InterState) {
    state.sideNav = !state.sideNav
  },
  setSideNavFalse(state: InterState) {
    state.sideNav = false
  },
  setShuffle(state: InterState) {
    state.isShuffle = !state.isShuffle
  },
  setShuffleTrue(state: InterState) {
    state.isShuffle = true
  },
  setRepeat(state: InterState) {
    state.isRepeat = !state.isRepeat
  },
  setRadio(state: InterState) {
    state.isRadio = !state.isRadio
  },
  setRadioGenre(state: InterState, payload: string) {
    state.radioGenre = payload
  },
  showFullTrue(state: InterState){
    state.showFull = true
  },
  showFullFalse(state: InterState){
    state.showFull = false
  },
  setRadioSource(state: InterState, payload: string) {
    state.radioSource = payload
    if (typeof window !== 'undefined') {
        window.localStorage.setItem('radioSource', payload)
    }
  },
  blacklistArtist(state: InterState, artistName: string) {
    if (!state.blacklistedArtists.includes(artistName)) {
        state.blacklistedArtists.push(artistName)
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(BLACKLIST_KEY, JSON.stringify(state.blacklistedArtists))
        }
        // Remove tracks of the blacklisted artist from the current queue
        state.tracksQueue = state.tracksQueue.filter((t: ArtistTrackModel) => t.artist_name !== artistName)
    }
  },
  playTrack(state: InterState, payload: ArtistTrackModel) {
    let indexToUpdate = -1
    state.tracksQueue.forEach((thisTrack: ArtistTrackModel, index: number) => {
        if (payload.id === thisTrack.id) {
            indexToUpdate = index
        }
    })
    state.current = state.tracksQueue[indexToUpdate]
    state.isRadio = false
  },
  setTracksQueue(state: InterState, payload: ArtistTrackModel[]) {
    state.tracksQueue = payload
  },
  setCurrentTrack(state: InterState, payload: ArtistTrackModel | null) {
    state.current = payload
    if (!payload || !payload.audio) {
      state.audio.removeAttribute('src')
      state.audio.load()
      return
    }

    if (state.audio.src !== payload.audio) {
      state.audio.src = payload.audio
      state.audio.load()
    }
  },
  markTrackUnavailable(state: InterState, payload: ArtistTrackModel | string) {
    const trackId = typeof payload === 'string' ? payload : payload.id

    if (!trackId) {
      return
    }

    if (!state.unavailableTrackIds.includes(trackId)) {
      state.unavailableTrackIds.push(trackId)
    }

    state.tracksQueue = state.tracksQueue.filter((track: ArtistTrackModel) => track.id !== trackId)

    if (state.current && state.current.id === trackId) {
      state.current = null
      state.audio.removeAttribute('src')
      state.audio.load()
    }
  },
  playNextSong(state: InterState) {
    if (!state.tracksQueue.length) {
      return
    }

    const indexToUpdate = getCurrentTrackIndex(state)
    if (state.isRepeat) {
      const repeatIndex = indexToUpdate >= 0 ? indexToUpdate : 0
      state.current = state.tracksQueue[repeatIndex]
      state.audio.src = state.tracksQueue[repeatIndex].audio
    } else if (state.isShuffle) {
      const random = getRandomTrackIndex(state, indexToUpdate)
      state.current = state.tracksQueue[random]
      state.audio.src = state.tracksQueue[random].audio
    } else{
      if (indexToUpdate === -1) {
        state.current = state.tracksQueue[0]
        state.audio.src = state.tracksQueue[0].audio
      } else if (indexToUpdate+1 <= state.tracksQueue.length - 1) {
        state.current = state.tracksQueue[indexToUpdate+1]
        state.audio.src = state.tracksQueue[indexToUpdate+1].audio
      } else {
        state.current = state.tracksQueue[0]
        state.audio.src = state.tracksQueue[0].audio
      }
    }
  },
  playPreviousSong(state: InterState) {
    if (!state.tracksQueue.length) {
      return
    }

    const indexToUpdate = getCurrentTrackIndex(state)
    if (state.isRepeat) {
      const repeatIndex = indexToUpdate >= 0 ? indexToUpdate : 0
      state.current = state.tracksQueue[repeatIndex]
      state.audio.src = state.tracksQueue[repeatIndex].audio
    } else if (state.isShuffle) {
      const random = getRandomTrackIndex(state, indexToUpdate)
      state.current = state.tracksQueue[random]
      state.audio.src = state.tracksQueue[random].audio
    } else {
      if (indexToUpdate <= 0) {
        state.current = state.tracksQueue[state.tracksQueue.length - 1]
        state.audio.src = state.tracksQueue[state.tracksQueue.length - 1].audio
      } else {
        state.current = state.tracksQueue[indexToUpdate-1]
        state.audio.src = state.tracksQueue[indexToUpdate -1].audio
      }
    }
  }
}

const getters = {
  getSideNav (state: InterState) {
    return state.sideNav
  },
  getAudio (state: InterState) {
    return state.audio
  },
  getCurrent (state: InterState) {
    return state.current
  },
  getShuffle(state: InterState) {
    return state.isShuffle
  },
  getRepeat (state: InterState) {
    return state.isRepeat
  },
  getRadio (state: InterState) {
    return state.isRadio
  },
  getRadioGenre (state: InterState) {
    return state.radioGenre
  },
  getShowFull (state: InterState) {
    return state.showFull
  },
  getBlacklistedArtists (state: InterState) {
    return state.blacklistedArtists
  },
  getRadioSource (state: InterState) {
    return state.radioSource
  }
}

const actions = {
  async playNextTrack({state, commit, dispatch}: {state: InterState; commit: Function; dispatch: Function}) {
    state.tracksQueue = state.tracksQueue.filter((track: ArtistTrackModel) => !isUnavailableTrack(state, track))

    if (!state.tracksQueue.length) {
      if (!state.isRadio) {
        return null
      }
      return dispatch('startRadioPlayback')
    }

    const indexToUpdate = getCurrentTrackIndex(state)

    if (state.isRepeat && state.current && !isUnavailableTrack(state, state.current)) {
      const repeatIndex = indexToUpdate >= 0 ? indexToUpdate : 0
      const repeatTrack = await findPlayableTrack(state, commit, [state.tracksQueue[repeatIndex]])
      if (repeatTrack) {
        commit('setCurrentTrack', repeatTrack)
        return repeatTrack
      }
    }

    if (state.isShuffle) {
      const randomIndex = getRandomTrackIndex(state, indexToUpdate)
      const shuffledTracks = rotateQueue(state.tracksQueue, randomIndex)
      const track = await findPlayableTrack(state, commit, shuffledTracks)
      if (track) {
        commit('setCurrentTrack', track)
        return track
      }
    }

    if (indexToUpdate >= 0) {
      const nextTracks = state.tracksQueue.slice(indexToUpdate + 1).concat(state.tracksQueue.slice(0, indexToUpdate + 1))
      const track = await findPlayableTrack(state, commit, nextTracks)
      if (track) {
        commit('setCurrentTrack', track)
        return track
      }
    }

    if (indexToUpdate === -1 && state.tracksQueue.length > 0) {
      const track = await findPlayableTrack(state, commit, state.tracksQueue)
      if (track) {
        commit('setCurrentTrack', track)
        return track
      }
    }

    if (state.isRadio) {
      return dispatch('startRadioPlayback')
    }

    state.audio.pause()
    state.audio.currentTime = 0
    return null
  },
  async playPreviousTrack({state, commit, dispatch}: {state: InterState; commit: Function; dispatch: Function}) {
    state.tracksQueue = state.tracksQueue.filter((track: ArtistTrackModel) => !isUnavailableTrack(state, track))

    if (!state.tracksQueue.length) {
      if (!state.isRadio) {
        return null
      }
      return dispatch('startRadioPlayback')
    }

    const indexToUpdate = getCurrentTrackIndex(state)

    if (state.isRepeat && state.current && !isUnavailableTrack(state, state.current)) {
      const repeatTrack = await findPlayableTrack(state, commit, [state.current])
      if (repeatTrack) {
        commit('setCurrentTrack', repeatTrack)
        return repeatTrack
      }
    }

    if (state.isShuffle) {
      const randomIndex = getRandomTrackIndex(state, indexToUpdate)
      const shuffledTracks = rotateQueue(state.tracksQueue, randomIndex)
      const track = await findPlayableTrack(state, commit, shuffledTracks)
      if (track) {
        commit('setCurrentTrack', track)
        return track
      }
    }

    const previousTracks = indexToUpdate <= 0
      ? state.tracksQueue.slice().reverse()
      : state.tracksQueue.slice(0, indexToUpdate).reverse().concat(state.tracksQueue.slice(indexToUpdate).reverse())

    const track = await findPlayableTrack(state, commit, previousTracks)
    if (track) {
      commit('setCurrentTrack', track)
      return track
    }

    if (state.isRadio) {
      return dispatch('startRadioPlayback')
    }

    return null
  },
  async startRadioPlayback({state, commit, dispatch}: {state: InterState; commit: Function; dispatch: Function}) {
    let tracks: ArtistTrackModel[] = []

    if (state.radioSource === 'suno') {
      const sunoLimit = 100
      tracks = await dispatch('sunoModule/fetchRadioTracks', {
        limit: sunoLimit,
        genre: state.radioGenre,
        excludeRecent: true,
        remember: true,
      })
    } else if (state.radioSource === 'audius') {
      // Audius: fetch a larger batch for more variety
      const audiusLimit = 100
      tracks = await dispatch('audiusModule/fetchRadioTracks', {
        limit: audiusLimit,
        genre: state.radioGenre,
        excludeRecent: true,
        remember: true,
      })
    } else {
      // Jamendo: diversify with different boost types and larger offset range
      const jamendoLimit = 100
      const boosts = ['popularity_total', 'popularity_month', 'popularity_week', 'listens_total', 'listens_month']
      const randomBoost = boosts[Math.floor(Math.random() * boosts.length)]
      
      tracks = await dispatch('trackModule/fetchRadioTracks', {
        limit: jamendoLimit,
        offset: createRadioOffset(jamendoLimit, 200), // Dig much deeper into the catalog
        boost: randomBoost,
        tags: state.radioGenre,
        excludeRecent: true,
        remember: true,
      })
    }

    if (!tracks || !tracks.length) {
      return null
    }

    // Filter out blacklisted artists
    const filteredTracks = tracks.filter((t: ArtistTrackModel) => (
      !state.blacklistedArtists.includes(t.artist_name) && !isUnavailableTrack(state, t)
    ))
    
    // Fallback if all fetched tracks happen to be blacklisted
    tracks = filteredTracks.length ? filteredTracks : tracks

    const randomIndex = Math.floor(Math.random() * tracks.length)

    if (state.radioSource === 'suno' || state.radioSource === 'audius' || state.radioSource === 'jamendo') {
      const rotatedTracks = rotateQueue(tracks, randomIndex)
      const playableTrack = await findPlayableTrack(state, commit, rotatedTracks)
      if (!playableTrack) {
        return null
      }
      const availableRotatedTracks = rotatedTracks.filter((track: ArtistTrackModel) => !isUnavailableTrack(state, track))
      const playableIndex = availableRotatedTracks.findIndex((track: ArtistTrackModel) => track.id === playableTrack.id)
      const playableQueue = rotateQueue(availableRotatedTracks, Math.max(0, playableIndex))
      commit('setTracksQueue', playableQueue)
      commit('setCurrentTrack', playableTrack)
      return playableTrack
    }

    const playableTrack = await findPlayableTrack(state, commit, rotateQueue(tracks, randomIndex))
    if (!playableTrack) {
      return null
    }

    commit('setTracksQueue', tracks.filter((track: ArtistTrackModel) => !isUnavailableTrack(state, track)))
    commit('setCurrentTrack', playableTrack)
    return playableTrack
  }
}
const store =  new Vuex.Store({
  state,
  mutations,
  getters,
  actions,
  modules: {
    artistModule,
    albumModule,
    trackModule,
    playlistModule,
    audiusModule,
    sunoModule,
  }
})

export default store;
